<script setup lang="ts">
import type { ProductDetails } from "mws-ts-v2-sdk";

import { BidAttemptType } from "~/types/Catalog";

type Emits = {
  (e: "back-to-card-overview"): void;
  (e: "raise-bid"): void;
  (e: "close"): void;
};

defineEmits<Emits>();

const { product } = defineProps<{
  amountInActiveCurrency: number;
  currency: string;
  product: ProductDetails;
  bidAttempt?: BidAttemptType;
}>();

const { amount: currentBidAmount } = usePrice(() => product.prices);
const { amount: nextMinimumBid } = usePrice(
  () => product.availableQuickBidPrices?.[0] ?? [],
);
</script>

<template>
  <BaseDialog
    :model-value="true"
    size="md"
    @close="$emit('close')"
  >
    <BaseDialogContent :fixed-height="true">
      <template #title>
        <button @click="$emit('back-to-card-overview')">
          <BaseIcon
            name="arrowLeft"
            :size="20"
          />
        </button>

        {{
          bidAttempt === BidAttemptType.Maxbid
            ? $t("product.raiseBid.titleMaxBid")
            : $t("product.raiseBid.title")
        }}
      </template>
      <template #content>
        <div class="flex flex-col gap-6">
          <p>{{ $t("product.raiseBid.info") }}</p>
          <div class="flex flex-col gap-4">
            <p class="uppercase font-bold text-lg">
              {{ $t("product.raiseBid.subtitle") }}
            </p>
            <div class="flex justify-between">
              <p class="uppercase font-bold text-platform-text-2">
                {{ $t("product.raiseBid.originalBid") }}
              </p>
              <BaseCurrency
                :iso="currency"
                :value="amountInActiveCurrency"
                :show-decimals="true"
              />
            </div>
            <div class="flex justify-between">
              <p class="uppercase font-bold text-platform-text-2">
                {{ $t("product.raiseBid.currentBid") }}
              </p>
              <BaseCurrency
                :iso="currentBidAmount.currencyCode"
                :value="currentBidAmount.value"
                :show-decimals="true"
              />
            </div>
            <div class="flex justify-between">
              <p class="uppercase font-bold text-platform-text-2">
                {{ $t("product.raiseBid.newBid") }}
              </p>
              <BaseCurrency
                :iso="nextMinimumBid.currencyCode"
                :value="nextMinimumBid.value"
                :show-decimals="true"
              />
            </div>
          </div>
        </div>
      </template>
      <template #buttons>
        <BaseButton @click="$emit('raise-bid')">
          {{
            bidAttempt === BidAttemptType.Maxbid
              ? $t("product.raiseBid.continueMaxBid")
              : $t("product.raiseBid.continue")
          }}
        </BaseButton>
      </template>
    </BaseDialogContent>
  </BaseDialog>
</template>
